// WP-1.2 (M6, S-09/S-10): transaction queries/mutations over the engine-backed API.
import {
  transactionDetailResponseSchema,
  transactionListResponseSchema,
  transactionResponseSchema,
  type TransactionCreateRequest,
  type TransactionDetailResponse,
  type TransactionResponse,
} from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from './api';
import { ACCOUNTS_KEY } from './accounts';

export const TRANSACTIONS_KEY = ['transactions'] as const;
export const transactionKey = (id: string): readonly [string, string] =>
  ['transactions', id] as const;

export interface TransactionFilters {
  accountId?: string;
  type?: 'expense' | 'income';
  category?: string;
  tag?: string;
  source?: string;
  from?: string;
  to?: string;
}

function filterQuery(filters: TransactionFilters): string {
  const params = new URLSearchParams();
  // Object.entries widens values to `any` here (TransactionFilters has no index signature)
  // — iterate typed keys instead so each value stays a real `string | undefined`.
  for (const key of Object.keys(filters) as (keyof TransactionFilters)[]) {
    const value = filters[key];
    if (value !== undefined && value !== '') params.set(key, value);
  }
  const qs = params.toString();
  return qs === '' ? '' : `?${qs}`;
}

export function useTransactions(
  filters: TransactionFilters = {},
): UseQueryResult<TransactionResponse[]> {
  return useQuery({
    queryKey: [...TRANSACTIONS_KEY, filters],
    queryFn: async () => {
      const body = transactionListResponseSchema.parse(
        await api.get(`/api/v1/transactions${filterQuery(filters)}`),
      );
      return body.transactions;
    },
  });
}

export function useTransaction(id: string): UseQueryResult<TransactionDetailResponse> {
  return useQuery({
    queryKey: transactionKey(id),
    queryFn: async () =>
      transactionDetailResponseSchema.parse(await api.get(`/api/v1/transactions/${id}`)),
  });
}

function useInvalidateTransactions(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: TRANSACTIONS_KEY }),
      queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY }), // balances changed too
    ]);
  };
}

export function useRecordTransaction(): UseMutationResult<
  TransactionResponse,
  Error,
  TransactionCreateRequest
> {
  const invalidate = useInvalidateTransactions();
  return useMutation({
    mutationFn: async (input: TransactionCreateRequest) =>
      transactionResponseSchema.parse(await api.post('/api/v1/transactions', input)),
    onSuccess: invalidate,
  });
}

export function useReverseTransaction(
  id: string,
): UseMutationResult<TransactionResponse, Error, string> {
  const invalidate = useInvalidateTransactions();
  return useMutation({
    mutationFn: async (reason: string) =>
      transactionResponseSchema.parse(
        await api.post(`/api/v1/transactions/${id}/reverse`, { reason }),
      ),
    onSuccess: invalidate,
  });
}

// PJ-7: unallocated income, relinked to a project after the fact.
export function useRelinkTransactionToProject(
  id: string,
): UseMutationResult<TransactionResponse, Error, string> {
  const invalidate = useInvalidateTransactions();
  return useMutation({
    mutationFn: async (projectId: string) =>
      transactionResponseSchema.parse(
        await api.post(`/api/v1/transactions/${id}/relink-project`, { projectId }),
      ),
    onSuccess: invalidate,
  });
}
